/**
 * S3 Service for frontend - handles file upload/download with S3 backend
 */

import { API_ENDPOINTS } from '../config/api';

export interface S3UploadResponse {
  code: number;
  status: string;
  data: {
    key: string;
    url: string;
    filename: string;
    size: number;
    contentType: string;
  };
}

export interface S3DownloadResponse {
  code: number;
  status: string;
  data: {
    url: string;
    expiresIn: number;
    key: string;
  };
}

export interface S3File {
  key: string;
  size: number;
  lastModified: string;
  filename: string;
}

export interface S3ListResponse {
  code: number;
  status: string;
  data: {
    files: S3File[];
    count: number;
  };
}

class S3Service {
  private getAuthHeaders(token: string) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Upload file to S3
   */
  async uploadFile(file: File, token: string): Promise<S3UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(API_ENDPOINTS.S3_UPLOAD, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type for FormData, let browser set it
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Upload failed');
    }

    return await response.json();
  }

  /**
   * Download file by S3 key (generates presigned URL)
   */
  async downloadFile(s3Key: string, token: string, expiresIn: number = 3600): Promise<string> {
    const response = await fetch(`${API_ENDPOINTS.S3_URL}/${encodeURIComponent(s3Key)}?expiresIn=${expiresIn}`, {
      method: 'GET',
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get download URL');
    }

    const result: S3DownloadResponse = await response.json();
    return result.data.url;
  }

  /**
   * Download file directly (opens in new tab)
   */
  async downloadFileDirect(s3Key: string, token: string, expiresIn: number = 3600): Promise<void> {
    const presignedUrl = await this.downloadFile(s3Key, token, expiresIn);
    window.open(presignedUrl, '_blank');
  }

  /**
   * Delete file from S3
   */
  async deleteFile(s3Key: string, token: string): Promise<void> {
    const response = await fetch(`${API_ENDPOINTS.S3_DELETE}/${encodeURIComponent(s3Key)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete file');
    }
  }

  /**
   * List user's files
   */
  async listFiles(token: string, prefix: string = ''): Promise<S3File[]> {
    const response = await fetch(`${API_ENDPOINTS.S3_LIST}?prefix=${encodeURIComponent(prefix)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to list files');
    }

    const result: S3ListResponse = await response.json();
    return result.data.files;
  }

  /**
   * Get presigned URL for file access
   */
  async getPresignedUrl(s3Key: string, token: string, expiresIn: number = 3600): Promise<string> {
    return await this.downloadFile(s3Key, token, expiresIn);
  }

  /**
   * Download file by CV ID (using backend proxy)
   */
  async downloadCVById(cvId: string, token: string, filename?: string): Promise<void> {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/user/cvs/download?id=${cvId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Download failed');
    }

    // Get filename from response headers or use provided filename
    const contentDisposition = response.headers.get('Content-Disposition');
    const downloadFilename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || filename || 'cv-download.pdf'
      : filename || 'cv-download.pdf';

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFilename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Check if a URL is an S3 key (not a full URL)
   */
  isS3Key(url: string): boolean {
    return !url.startsWith('http') && url.includes('/');
  }

  /**
   * Extract filename from S3 key
   */
  getFilenameFromS3Key(s3Key: string): string {
    return s3Key.split('/').pop() || 'unknown-file';
  }
}

export const s3Service = new S3Service();
export default s3Service;
